import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { getUuid } from '@front10/helpers/dist/uuid';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';

import Box from '../../elements/Box';
import Select from '../../elements/Select';
import Text from '../../elements/Text';

function formatOptions(options, optionsLabelsMap, translate) {
  const array = options.map((option) => {
    return {
      name: translate(optionsLabelsMap[option], option),
      value: option,
      key: option
    };
  });
  return array;
}

const FareSimpleSorting = React.forwardRef((props, receivedRef) => {
  const id = getUuid();
  const {
    options,
    selectedOption,
    fullWidth,
    onChange,
    fareSimpleSortingLabel,
    fareSimpleSortingSelect,
    sortingOptionsLabelsMap,
    dataTestId,
    showLabel
  } = props;
  const { translate, direction, theme } = useContext(Context);
  const ownRef = useRef();
  const ref = receivedRef || ownRef;
  const [className, style] = useStyles(props, { direction, theme });

  return (
    <Box className={`fr-clearfix ${className}`} style={style} dataTestId={dataTestId}>
      <Box {...(!fullWidth ? { floatRight: true } : {})}>
        {showLabel && (
          <Text
            paddingTop="2"
            paddingRight="2"
            {...fareSimpleSortingLabel}
            floatLeft
            type="label"
            htmlFor={`fare-simple-sorting-${id}`}
          >
            {translate('cmp-sort-by-label', 'Sort by:')}
          </Text>
        )}
        <Select
          {...fareSimpleSortingSelect}
          ref={ref}
          id={`fare-simple-sorting-${id}`}
          ariaLabel={translate(
            'cmp-simple-sorting-aria-label',
            'Press the UP/Down Arrow key to choose a sorting criteria, press the Enter key to select the item or press the TAB key to continue'
          )}
          ariaDescribedBy={`fare-simple-sorting-description-${id}`}
          options={formatOptions(options, sortingOptionsLabelsMap, translate)}
          value={selectedOption}
          fullWidth={fullWidth}
          onChange={onChange}
        />
        <p id={`fare-simple-sorting-description-${id}`} className="fr-sr-only" aria-hidden="true">
          {translate(
            'cmp-simple-sorting-aria-label-description',
            'Your are in the Fare Simple Sorting'
          )}
        </p>
      </Box>
    </Box>
  );
});

FareSimpleSorting.propTypes = {
  /**
   * Options of sorting
   */
  options: PropTypes.arrayOf(PropTypes.string),
  /**
   * Selected option of sorting
   */
  selectedOption: PropTypes.string,
  /**
   * View in full width
   */
  fullWidth: PropTypes.bool,
  /**
   * Function fired when onChange the select
   */
  onChange: PropTypes.func,
  /**
   * Settings for fareSimpleSortingLabel
   */
  fareSimpleSortingLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for fareSimpleSortingSelect
   */
  fareSimpleSortingSelect: PropTypes.objectOf(PropTypes.any),
  /**
   * Labels keys for the sorting options
   */
  sortingOptionsLabelsMap: PropTypes.objectOf(PropTypes.any),
  /**
   * Data test ID of Sorting
   */
  dataTestId: PropTypes.string,
  /**
   * Show or hide label
   */
  showLabel: PropTypes.bool
};

FareSimpleSorting.defaultProps = {
  dataTestId: '',
  options: ['price_low_to_hight', 'price_hight_to_low', 'departure_date', 'no_sorting'],
  selectedOption: 'price_low_to_hight',
  sortingOptionsLabelsMap: {
    price_low_to_hight: 'fare_price_low_to_high_sorting_label',
    price_hight_to_low: 'fare_price_high_to_low_sorting_label',
    departure_date: 'fare_date_sorting_label',
    no_sorting: 'cmp-blank-option-label',
    most_popular: 'fare_most_popular_sorting_label'
  },
  fullWidth: false,
  onChange: () => {},
  fareSimpleSortingLabel: {},
  fareSimpleSortingSelect: {},
  showLabel: true
};

FareSimpleSorting.componentName = 'FareSimpleSorting';

export default FareSimpleSorting;
